import {
  ActiveFilter,
  ActiveFilterPills,
} from "@/shared/components/common/ActiveFilterPills";
import {
  EntityListFilter,
  EntityListHeaderShell,
} from "@/features/tenantUsers/components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getStudentsListFilterSections } from "../constants/filterConfig";
import { getStudentsListingConfig } from "../constants/listingConfig";
import { initialStudentListFilters, useStudentsListStore } from "@/store";
import { useStudentInstitutionOptions } from "../hooks/useStudentLookupOptions";
import { StudentInstituteFilter } from "./StudentInstituteFilter";

export function StudentsHeader() {
  const { t } = useTranslation(["students", "common"]);
  const listFilterValues = useStudentsListStore((s) => s.listFilterValues);
  const setListFilterValues = useStudentsListStore(
    (s) => s.setListFilterValues,
  );
  const { setPage: setCurrentPage } = useStudentsListStore();
  const searchQuery = useStudentsListStore((s) => s.searchQuery);
  const setSearchQuery = useStudentsListStore((s) => s.setSearchQuery);

  const instituteId =
    typeof listFilterValues.institute_id === "string"
      ? listFilterValues.institute_id.trim()
      : "";
  const { data: instituteOptions } = useStudentInstitutionOptions();

  const listingConfig = useMemo(() => getStudentsListingConfig(t), [t]);
  const filterSections = useMemo(() => getStudentsListFilterSections(t), [t]);

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const a: ActiveFilter[] = [];
    for (const key of ["board", "class", "section"] as const) {
      const value = listFilterValues[key];
      if (typeof value === "string" && value.trim()) {
        a.push({
          id: key,
          label: t(`students:filter.${key}`),
          displayText: value.trim(),
        });
      }
    }
    if (instituteId) {
      a.push({
        id: "institute_id",
        label: t("students:filter.institute"),
        displayText:
          instituteOptions?.find((o) => o.value === instituteId)?.label ??
          instituteId,
      });
    }
    const accountStatus =
      typeof listFilterValues.accountStatus === "string"
        ? listFilterValues.accountStatus
        : "";
    if (accountStatus) {
      a.push({
        id: "accountStatus",
        label: t("students:filter.status"),
        displayText: accountStatus,
      });
    }
    return a;
  }, [listFilterValues, instituteId, instituteOptions, t]);

  const handleInstituteChange = (id: string) => {
    setListFilterValues({ ...listFilterValues, institute_id: id });
    setCurrentPage(1);
  };

  const handleRemoveFilter = (id: string) => {
    setListFilterValues({ ...listFilterValues, [id]: "" });
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setListFilterValues({ ...initialStudentListFilters });
    setSearchQuery("");
    setCurrentPage(1);
  };

  return (
    <EntityListHeaderShell
      searchPlaceholder={listingConfig.searchPlaceholder}
      sampleSheetPath={listingConfig.sampleSheetPath}
      bulkUploadPath={listingConfig.bulkUploadPath}
      addPath={listingConfig.addPath}
      addLabel={listingConfig.addLabel}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      filterControl={
        <>
          <StudentInstituteFilter
            value={instituteId}
            onChange={handleInstituteChange}
          />
          <EntityListFilter
            sections={filterSections}
            values={listFilterValues}
            onChange={(next) =>
              // The popover only knows its own sections; keep the institute selection.
              setListFilterValues({ ...next, institute_id: instituteId })
            }
            onApply={() => setCurrentPage(1)}
            activeFilters={activeFilters}
            onRemoveFilter={handleRemoveFilter}
            onClearAllFilters={handleClearAllFilters}
            showPills={false}
          />
        </>
      }
      footerContent={
        <ActiveFilterPills
          filters={activeFilters}
          onRemove={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      }
    />
  );
}
